/**
 * TeamList - Team management with company aliases
 *
 * Features:
 * - Table view with sorting on all columns
 * - Filters: sport, type, has alias, search
 * - Company-specific alias overrides
 * - Create local teams
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchTeams,
  createTeam,
  updateTeam,
  deleteTeam,
  fetchTeamMembers,
  searchMatchHubTeams,
  linkTeamToMatchHub,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import MatchHubSearchInput from '../../../../shared/components/MatchHubSearchInput';
import styles from './TeamList.module.css';

// Relative-time formatter used by the BT-4 "Last seen" column. Inline
// rather than imported because Tank doesn't have a shared time util yet
// and this is the only consumer at the moment.
function formatRelativeTime(iso) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 30) return `${Math.floor(diffSec / 86400)}d ago`;
  if (diffSec < 86400 * 365) return `${Math.floor(diffSec / (86400 * 30))}mo ago`;
  return `${Math.floor(diffSec / (86400 * 365))}y ago`;
}

function SortHeader({ label, sortKeyName, sortKey, sortDir, onSort }) {
  return (
    <th
      className={clsx(styles.th, styles.sortable)}
      onClick={() => onSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (
        <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ^' : ' v'}</span>
      )}
    </th>
  );
}

const TEAM_TYPES = [
  { key: 'all', label: 'All Types' },
  { key: 'club', label: 'Club' },
  { key: 'national', label: 'National' },
  { key: 'other', label: 'Other' },
];

const SPORTS = [
  { key: 'all', label: 'All Sports' },
  { key: 'football', label: 'Football' },
  { key: 'basketball', label: 'Basketball' },
  { key: 'hockey', label: 'Hockey' },
  { key: 'tennis', label: 'Tennis' },
  { key: 'other', label: 'Other' },
];

function TeamDrawer({ team, onSave, onClose, isNew = false }) {
  const [form, setForm] = useState({
    name: team?.name || team?.global_name || '',
    short_name: team?.short_name || '',
    team_type: team?.team_type || 'club',
    sport: team?.sport || 'football',
    notes: team?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  // Round 2: inline MatchHub link sub-flow. `linkPick` holds a chosen MatchHub
  // team while the editor edits the override name before confirming.
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkPick, setLinkPick] = useState(null); // {id, name} | null
  const [linkOverrideName, setLinkOverrideName] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkedExternalId, setLinkedExternalId] = useState(team?.external_id || '');

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save team:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (team?.global_name) {
      setForm((prev) => ({
        ...prev,
        name: team.global_name,
        short_name: '',
        notes: '',
      }));
    }
  };

  const handleConfirmLink = async () => {
    if (!team?.id || !linkPick) return;
    const name = linkOverrideName.trim();
    if (!name) {
      setLinkError('Override name is required');
      return;
    }
    setLinking(true);
    setLinkError('');
    try {
      const res = await linkTeamToMatchHub(team.id, linkPick.id, name);
      setLinkedExternalId(res?.external_id || String(linkPick.id));
      setForm((prev) => ({ ...prev, name }));
      setLinkOpen(false);
      setLinkPick(null);
      setLinkOverrideName('');
    } catch (err) {
      setLinkError(err.response?.data?.error || err.message || 'Link failed');
    } finally {
      setLinking(false);
    }
  };

  const renderMatchHubRow = (item) => (
    <div style={{ padding: '6px 10px' }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{item.name}</div>
      <div style={{ fontSize: 11, opacity: 0.6 }}>
        {[item.country, item.code].filter(Boolean).join(' · ')}
      </div>
    </div>
  );

  const body = (
    <>
      {!isNew && team?.global_name && (
        <div className={styles.globalInfo}>
          <span className={styles.globalLabel}>Global Team:</span>
          <span className={styles.globalValue}>{team.global_name}</span>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.label}>Display Name</label>
        <input
          type="text"
          className={styles.input}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Team display name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Short Name</label>
        <input
          type="text"
          className={styles.input}
          value={form.short_name}
          onChange={(e) => setForm({ ...form, short_name: e.target.value })}
          placeholder="e.g., FEY, AJX"
          maxLength={5}
        />
      </div>

      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Type</label>
          <select
            className={styles.select}
            value={form.team_type}
            onChange={(e) => setForm({ ...form, team_type: e.target.value })}
          >
            <option value="club">Club</option>
            <option value="national">National</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Sport</label>
          <select
            className={styles.select}
            value={form.sport}
            onChange={(e) => setForm({ ...form, sport: e.target.value })}
          >
            <option value="football">Football</option>
            <option value="basketball">Basketball</option>
            <option value="hockey">Hockey</option>
            <option value="tennis">Tennis</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Notes</label>
        <textarea
          className={styles.textarea}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          placeholder="Internal notes..."
          rows={2}
        />
      </div>

      {/* Round 2 — MatchHub link. Editor explicitly picks the MatchHub team
          and confirms the per-company override name. We never auto-link. */}
      {!isNew && (
        <div className={styles.formGroup}>
          <label className={styles.label}>MatchHub link</label>
          {linkedExternalId ? (
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Linked to MatchHub team id <strong>{linkedExternalId}</strong>.
              {' '}
              <Btn kind="ghost" size="xs" onClick={() => setLinkOpen(true)}>
                Re-link
              </Btn>
            </div>
          ) : !linkOpen ? (
            <Btn kind="ghost" size="sm" onClick={() => setLinkOpen(true)}>
              Link to MatchHub…
            </Btn>
          ) : !linkPick ? (
            <MatchHubSearchInput
              searchFn={(q) => searchMatchHubTeams(q)}
              dataKey="teams"
              renderRow={renderMatchHubRow}
              onSelect={(t) => {
                setLinkPick({ id: t.id, name: t.name });
                setLinkOverrideName(t.name || '');
              }}
              placeholder="Search MatchHub for this team…"
              inputClassName={styles.input}
            />
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="text"
                className={styles.input}
                value={linkOverrideName}
                onChange={(e) => setLinkOverrideName(e.target.value)}
                placeholder="Override name (e.g. Excelsior Rotterdam)"
                style={{ flex: 1 }}
              />
              <Btn kind="primary" size="sm" onClick={handleConfirmLink} disabled={linking}>
                {linking ? 'Linking…' : 'Confirm link'}
              </Btn>
              <Btn kind="ghost" size="sm" onClick={() => { setLinkPick(null); setLinkOverrideName(''); }}>
                Back
              </Btn>
            </div>
          )}
          {linkError && (
            <div style={{ fontSize: 12, color: 'var(--danger, #ff6b6b)', marginTop: 6 }}>
              {linkError}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow={isNew ? '§ TEAM · NEW' : '§ TEAM'}
      title={isNew ? 'New team' : (team?.name || 'Team')}
      subtitle={!isNew && team?.global_name ? `Alias of ${team.global_name}` : undefined}
      ariaLabel={isNew ? 'Create team' : 'Edit team'}
      body={body}
      footer={(
        <>
          {!isNew && team?.global_name && (
            <Btn kind="ghost" size="sm" onClick={handleReset}>
              Reset to global
            </Btn>
          )}
          <span className={styles.footerSpacer} />
          <Btn kind="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <Btn
            kind="primary"
            size="sm"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </Btn>
        </>
      )}
    />
  );
}

function MembersRow({ teamId, colSpan }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      try {
        const data = await fetchTeamMembers(teamId);
        setMembers(data || []);
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };
    loadMembers();
  }, [teamId]);

  if (loading) {
    return (
      <tr className={styles.membersRow}>
        <td colSpan={colSpan} className={styles.membersCell}>
          <div className={styles.membersLoading}>
            <Spinner />
            <span>Loading members...</span>
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr className={styles.membersRow}>
        <td colSpan={colSpan} className={styles.membersCell}>
          <div className={styles.membersError}>Failed to load members: {error}</div>
        </td>
      </tr>
    );
  }

  if (members.length === 0) {
    return (
      <tr className={styles.membersRow}>
        <td colSpan={colSpan} className={styles.membersCell}>
          <div className={styles.membersEmpty}>No members in this team</div>
        </td>
      </tr>
    );
  }

  return (
    <tr className={styles.membersRow}>
      <td colSpan={colSpan} className={styles.membersCell}>
        <div className={styles.membersGrid}>
          {members.map((m) => (
            <div key={m.id} className={styles.memberCard}>
              {m.jersey_number && (
                <span className={styles.memberJersey}>#{m.jersey_number}</span>
              )}
              <span className={styles.memberName}>{m.display_name}</span>
              {m.first_name && m.last_name && (
                <span className={styles.memberFullName}>
                  {m.first_name} {m.last_name}
                </span>
              )}
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

export default function TeamList() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Expanded teams (for showing members)
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [aliasFilter, setAliasFilter] = useState('all'); // all, has_alias, no_alias

  // Sorting
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [isNewTeam, setIsNewTeam] = useState(false);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTeams({ limit: 500 });
      setTeams(data.results || data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  // Filter and sort teams
  const filteredTeams = useMemo(() => {
    let result = teams;

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.name?.toLowerCase().includes(term) ||
          t.global_name?.toLowerCase().includes(term) ||
          t.short_name?.toLowerCase().includes(term)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      result = result.filter((t) => t.team_type === typeFilter);
    }

    // Sport filter
    if (sportFilter !== 'all') {
      result = result.filter((t) => t.sport === sportFilter);
    }

    // Alias filter
    if (aliasFilter === 'has_alias') {
      result = result.filter((t) => t.has_alias || t.name !== t.global_name);
    } else if (aliasFilter === 'no_alias') {
      result = result.filter((t) => !t.has_alias && t.name === t.global_name);
    }

    // Sort
    result = [...result].sort((a, b) => {
      const rawA = a[sortKey] || '';
      const rawB = b[sortKey] || '';
      const aVal = typeof rawA === 'string' ? rawA.toLowerCase() : rawA;
      const bVal = typeof rawB === 'string' ? rawB.toLowerCase() : rawB;

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [teams, search, typeFilter, sportFilter, aliasFilter, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setIsNewTeam(false);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTeam(null);
    setIsNewTeam(true);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    if (isNewTeam) {
      await createTeam(formData.name, formData.team_type);
    } else if (editingTeam) {
      await updateTeam(editingTeam.id, formData);
    }
    loadTeams();
  };

  const handleDelete = async (team) => {
    if (!confirm(`Delete team "${team.name}"? This cannot be undone.`)) return;
    try {
      await deleteTeam(team.id);
      loadTeams();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const toggleExpanded = (teamId) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  if (loading && teams.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading teams...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Teams</h2>
          <p className={styles.subtitle}>
            Manage team display names and aliases for your company
          </p>
        </div>
        <Btn kind="primary" size="sm" onClick={handleCreate}>
          + New team
        </Btn>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search teams..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {TEAM_TYPES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
        >
          {SPORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={aliasFilter}
          onChange={(e) => setAliasFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="has_alias">Has Alias</option>
          <option value="no_alias">No Alias</option>
        </select>

        <span className={styles.count}>{filteredTeams.length} teams</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}></th>
              <SortHeader label="Name" sortKeyName="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Members" sortKeyName="member_count" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Type" sortKeyName="team_type" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              {/* BT-4 — last face-embedding capture across team members. */}
              <SortHeader label="Last seen" sortKeyName="last_seen" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTeams.map((team) => {
              const isExpanded = expandedTeams.has(team.id);
              return (
                <React.Fragment key={team.id}>
                  <tr className={clsx(styles.row, isExpanded && styles.expanded)}>
                    <td className={styles.td}>
                      <button
                        className={styles.expandBtn}
                        onClick={() => toggleExpanded(team.id)}
                        title={isExpanded ? 'Collapse' : 'Show members'}
                      >
                        {isExpanded ? 'v' : '>'}
                      </button>
                    </td>
                    <td className={styles.td}>
                      <span className={styles.teamCell}>
                        {/* 28×28 logo placeholder — 3-letter mono block. Source:
                            design_handoff_tank manage-redesign-v2 brief. */}
                        <span className={styles.logoPlaceholder} aria-hidden="true">
                          {(team.short_name || team.name || '?')
                            .slice(0, 3)
                            .toUpperCase()}
                        </span>
                        <span className={styles.teamName}>{team.name}</span>
                        {team.name !== team.global_name && (
                          <span className={styles.aliasBadge}>Alias</span>
                        )}
                      </span>
                    </td>
                    <td className={styles.td}>
                      {/* Empty-team warning state per design spec — warn color
                          + "empty" tag when member_count === 0. */}
                      {(team.member_count ?? 0) === 0 ? (
                        <span className={styles.memberCellEmpty}>
                          <span className={styles.memberCount}>0</span>
                          <span className={styles.emptyTag}>empty</span>
                        </span>
                      ) : (
                        <span className={styles.memberCount}>
                          {team.member_count}
                        </span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <span className={styles.typeBadge}>{team.team_type || 'club'}</span>
                    </td>
                    <td className={styles.td}>
                      {/* BT-4 — relative timestamp; faint when no embeddings. */}
                      {team.last_seen ? (
                        <span className={styles.lastSeen}>
                          {formatRelativeTime(team.last_seen)}
                        </span>
                      ) : (
                        <span className={styles.lastSeenNever}>—</span>
                      )}
                    </td>
                    <td className={styles.td}>
                      <div className={styles.actions}>
                        <Btn kind="ghost" size="sm" onClick={() => handleEdit(team)}>
                          Edit
                        </Btn>
                        <Btn kind="danger" size="sm" onClick={() => handleDelete(team)}>
                          Delete
                        </Btn>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && <MembersRow teamId={team.id} colSpan={6} />}
                </React.Fragment>
              );
            })}
            {filteredTeams.length === 0 && (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  No teams found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <TeamDrawer
          team={editingTeam}
          isNew={isNewTeam}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTeam(null);
          }}
        />
      )}
    </div>
  );
}
